import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { agentService } from "@/services/agent.service/agent.api";
import clientService from "@/services/client";
import companyService from "@/services/company";
import projectService from "@/services/project";
import { teamService } from "@/services/team.service/team.api";
import ticketService, { Ticket } from "@/services/ticket";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function NewTicket() {
    const navigate = useNavigate();
    const { toast } = useToast();

    // Form fields
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState<Ticket["priority"]>("medium");
    const [selectedCompany, setSelectedCompany] = useState("");
    const [selectedProject, setSelectedProject] = useState("");
    const [selectedContact, setSelectedContact] = useState("");
    const [selectedTeam, setSelectedTeam] = useState("");
    const [selectedAgent, setSelectedAgent] = useState("");

    // Options
    const [companies, setCompanies] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [contacts, setContacts] = useState<any[]>([]);
    const [teams, setTeams] = useState<any[]>([]);
    const [agents, setAgents] = useState<any[]>([]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        loadCompanies();
        loadTeams();
        loadAgents();
    }, []);

    // Cascade: reload projects and contacts when company changes
    useEffect(() => {
        if (selectedCompany) {
            loadProjects(selectedCompany);
            loadContacts(selectedCompany);
        } else {
            setProjects([]);
            setContacts([]);
            setSelectedProject("");
            setSelectedContact("");
        }
    }, [selectedCompany]);

    const loadCompanies = async () => {
        const [data] = await companyService.getAll(undefined, 1, 500);
        setCompanies(data?.result || []);
    };

    const loadProjects = async (companyId: string) => {
        const [data] = await projectService.getAllByClient(companyId, undefined, 1, 500);
        setProjects(data?.result || []);
    };

    const loadContacts = async (companyId: string) => {
        const [data] = await clientService.getAll(undefined, 1, 500, undefined, companyId);
        setContacts(data?.result || []);
    };

    const loadTeams = async () => {
        const [data] = await teamService.getAll({}, undefined as unknown as number, undefined as unknown as number);
        setTeams(data?.result || []);
    };

    const loadAgents = async () => {
        const [data] = await agentService.getResponsibles({}, undefined, undefined);
        setAgents(data?.result || []);
    };

    const handleSubmit = async () => {
        if (!subject.trim()) {
            toast({
                title: "Campo obrigatório",
                description: "Preencha o assunto do ticket.",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);
        const [data, error] = await ticketService.create({
            subject: subject.trim(),
            description: description.trim(),
            priority,
            clientId: selectedCompany || undefined,
            projectId: selectedProject || undefined,
            contactId: selectedContact || undefined,
            teamId: selectedTeam || undefined,
            agentId: selectedAgent || undefined,
        });
        setIsSubmitting(false);

        if (error) {
            toast({
                title: "Erro ao criar ticket",
                description:
                    (error as any)?.message ||
                    (error as any)?.msg ||
                    "Não foi possível criar o ticket.",
                variant: "destructive",
            });
            return;
        }

        if (data) {
            toast({
                title: "Ticket criado",
                description: "O ticket foi criado com sucesso.",
            });
            navigate(`/tickets/${data.protocolId || data._id}`);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div className="flex items-center gap-3">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate("/tickets")}
                    className="hover:bg-transparent"
                >
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-2xl font-bold">Novo Ticket</h1>
            </div>

            <div className="rounded-lg border bg-card p-6 space-y-5">
                {/* Assunto */}
                <div className="space-y-2">
                    <Label htmlFor="subject">
                        Assunto <span className="text-destructive">*</span>
                    </Label>
                    <Input
                        id="subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Digite o assunto do ticket"
                    />
                </div>

                {/* Descrição */}
                <div className="space-y-2">
                    <Label htmlFor="description">Descrição</Label>
                    <Textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Descreva o problema ou solicitação"
                        rows={4}
                    />
                </div>

                {/* Prioridade */}
                <div className="space-y-2">
                    <Label htmlFor="priority">Prioridade</Label>
                    <Select
                        value={priority}
                        onValueChange={(v) => setPriority(v as Ticket["priority"])}
                    >
                        <SelectTrigger id="priority">
                            <SelectValue placeholder="Selecione a prioridade" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="low">Baixa</SelectItem>
                            <SelectItem value="medium">Média</SelectItem>
                            <SelectItem value="high">Alta</SelectItem>
                            <SelectItem value="urgent">Urgente</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Empresa */}
                <div className="space-y-2">
                    <Label htmlFor="company">Empresa</Label>
                    <Select
                        value={selectedCompany || undefined}
                        onValueChange={(v) => {
                            setSelectedCompany(v);
                            setSelectedProject("");
                            setSelectedContact("");
                        }}
                    >
                        <SelectTrigger id="company">
                            <SelectValue placeholder="Selecione uma empresa" />
                        </SelectTrigger>
                        <SelectContent>
                            {companies.map((c) => (
                                <SelectItem key={c._id} value={c._id}>
                                    {c.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Projeto */}
                <div className="space-y-2">
                    <Label htmlFor="project">Projeto</Label>
                    <Select
                        value={selectedProject || undefined}
                        onValueChange={setSelectedProject}
                        disabled={!selectedCompany}
                    >
                        <SelectTrigger id="project">
                            <SelectValue
                                placeholder={
                                    selectedCompany
                                        ? "Selecione um projeto"
                                        : "Selecione uma empresa primeiro"
                                }
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {projects.map((p) => (
                                <SelectItem key={p._id} value={p._id}>
                                    {p.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Pessoa */}
                <div className="space-y-2">
                    <Label htmlFor="contact">Pessoa</Label>
                    <Select
                        value={selectedContact || undefined}
                        onValueChange={setSelectedContact}
                        disabled={!selectedCompany}
                    >
                        <SelectTrigger id="contact">
                            <SelectValue
                                placeholder={
                                    selectedCompany
                                        ? "Selecione uma pessoa"
                                        : "Selecione uma empresa primeiro"
                                }
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {contacts.map((c) => (
                                <SelectItem key={c._id} value={c._id}>
                                    {c.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Equipe */}
                <div className="space-y-2">
                    <Label htmlFor="team">Equipe</Label>
                    <Select
                        value={selectedTeam || undefined}
                        onValueChange={setSelectedTeam}
                    >
                        <SelectTrigger id="team">
                            <SelectValue placeholder="Selecione uma equipe" />
                        </SelectTrigger>
                        <SelectContent>
                            {teams.map((t) => (
                                <SelectItem key={t._id} value={t._id}>
                                    {t.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Responsável */}
                <div className="space-y-2">
                    <Label htmlFor="agent">Responsável</Label>
                    <Select
                        value={selectedAgent || undefined}
                        onValueChange={setSelectedAgent}
                    >
                        <SelectTrigger id="agent">
                            <SelectValue placeholder="Selecione um responsável" />
                        </SelectTrigger>
                        <SelectContent>
                            {agents.map((a) => (
                                <SelectItem key={a._id} value={a._id}>
                                    {a.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                    <Button
                        variant="outline"
                        onClick={() => navigate("/tickets")}
                        className="flex-1"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex-1"
                    >
                        {isSubmitting ? "Criando..." : "Criar Ticket"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
